import axios from 'axios';

const RAPIDAPI_BASE_URL = 'https://gym-fit.p.rapidapi.com';
const RAPIDAPI_KEY = import.meta.env?.VITE_RAPIDAPI_GYM_FIT_KEY;

// MyFitnessPal API Configuration
const MYFITNESSPAL_BASE_URL = 'https://myfitnesspal2.p.rapidapi.com';
const MYFITNESSPAL_API_KEY = import.meta.env?.VITE_RAPIDAPI_MYFITNESSPAL_KEY;

// Create axios instance with RapidAPI configuration
const rapidApiClient = axios?.create({
  baseURL: RAPIDAPI_BASE_URL,
  headers: {
    'x-rapidapi-host': 'gym-fit.p.rapidapi.com',
    'x-rapidapi-key': RAPIDAPI_KEY
  },
  timeout: 10000
});

// Create axios instance for MyFitnessPal API
const myFitnessPalClient = axios?.create({
  baseURL: MYFITNESSPAL_BASE_URL,
  headers: {
    'x-rapidapi-host': 'myfitnesspal2.p.rapidapi.com',
    'x-rapidapi-key': MYFITNESSPAL_API_KEY
  },
  timeout: 10000
});

/**
 * RapidAPI Gym-Fit Service
 * Provides access to comprehensive exercise database
 */
export const rapidApiGymFitService = {
  /**
   * Search exercises by name or keyword
   * @param {string} query - Search query
   * @returns {Promise} Exercise results
   */
  searchExercises: async (query) => {
    try {
      const response = await rapidApiClient?.get('/exercises', {
        params: { name: query }
      });
      return {
        data: response?.data?.map(exercise => ({
          id: `api-${exercise?.id}`,
          name: exercise?.name,
          description: exercise?.description || '',
          category: exercise?.category?.toLowerCase() || 'strength',
          difficulty: exercise?.difficulty?.toLowerCase() || 'intermediate',
          muscleGroups: exercise?.primaryMuscles || [],
          secondaryMuscles: exercise?.secondaryMuscles || [],
          equipmentNeeded: exercise?.equipment ? [exercise?.equipment] : [],
          instructions: exercise?.instructions || [],
          videoUrl: exercise?.videoUrl || null,
          imageUrl: exercise?.images?.[0] || null,
          source: 'rapidapi',
          isExternal: true
        })) || [],
        error: null
      };
    } catch (error) {
      console.error('RapidAPI search error:', error);
      return {
        data: [],
        error: error?.response?.data?.message || error?.message || 'Failed to search exercises'
      };
    }
  },

  /**
   * Get exercises by muscle group
   * @param {string} muscleGroup - Target muscle group
   * @returns {Promise} Exercise results
   */
  getExercisesByMuscle: async (muscleGroup) => {
    try {
      const response = await rapidApiClient?.get('/exercises', {
        params: { muscle: muscleGroup }
      });
      return {
        data: response?.data?.map(exercise => ({
          id: `api-${exercise?.id}`,
          name: exercise?.name,
          description: exercise?.description || '',
          category: exercise?.category?.toLowerCase() || 'strength',
          difficulty: exercise?.difficulty?.toLowerCase() || 'intermediate',
          muscleGroups: exercise?.primaryMuscles || [],
          secondaryMuscles: exercise?.secondaryMuscles || [],
          equipmentNeeded: exercise?.equipment ? [exercise?.equipment] : [],
          instructions: exercise?.instructions || [],
          videoUrl: exercise?.videoUrl || null,
          imageUrl: exercise?.images?.[0] || null,
          source: 'rapidapi',
          isExternal: true
        })) || [],
        error: null
      };
    } catch (error) {
      console.error('RapidAPI muscle group error:', error);
      return {
        data: [],
        error: error?.response?.data?.message || error?.message || 'Failed to fetch exercises'
      };
    }
  },

  /**
   * Get exercises by equipment
   * @param {string} equipment - Equipment type
   * @returns {Promise} Exercise results
   */
  getExercisesByEquipment: async (equipment) => {
    try {
      const response = await rapidApiClient?.get('/exercises', {
        params: { equipment: equipment }
      });
      return {
        data: response?.data?.map(exercise => ({
          id: `api-${exercise?.id}`,
          name: exercise?.name,
          description: exercise?.description || '',
          category: exercise?.category?.toLowerCase() || 'strength',
          difficulty: exercise?.difficulty?.toLowerCase() || 'intermediate',
          muscleGroups: exercise?.primaryMuscles || [],
          secondaryMuscles: exercise?.secondaryMuscles || [],
          equipmentNeeded: exercise?.equipment ? [exercise?.equipment] : [],
          instructions: exercise?.instructions || [],
          videoUrl: exercise?.videoUrl || null,
          imageUrl: exercise?.images?.[0] || null,
          source: 'rapidapi',
          isExternal: true
        })) || [],
        error: null
      };
    } catch (error) {
      console.error('RapidAPI equipment error:', error);
      return {
        data: [],
        error: error?.response?.data?.message || error?.message || 'Failed to fetch exercises'
      };
    }
  },

  /**
   * Get exercise details by ID
   * @param {string} exerciseId - Exercise ID
   * @returns {Promise} Exercise details
   */
  getExerciseById: async (exerciseId) => {
    try {
      const cleanId = exerciseId?.replace('api-', '');
      const response = await rapidApiClient?.get(`/exercises/${cleanId}`);
      const exercise = response?.data;
      return {
        data: {
          id: `api-${exercise?.id}`,
          name: exercise?.name,
          description: exercise?.description || '',
          category: exercise?.category?.toLowerCase() || 'strength',
          difficulty: exercise?.difficulty?.toLowerCase() || 'intermediate',
          muscleGroups: exercise?.primaryMuscles || [],
          secondaryMuscles: exercise?.secondaryMuscles || [],
          equipmentNeeded: exercise?.equipment ? [exercise?.equipment] : [],
          instructions: exercise?.instructions || [],
          videoUrl: exercise?.videoUrl || null,
          imageUrl: exercise?.images?.[0] || null,
          source: 'rapidapi',
          isExternal: true
        },
        error: null
      };
    } catch (error) {
      console.error('RapidAPI exercise details error:', error);
      return {
        data: null,
        error: error?.response?.data?.message || error?.message || 'Failed to fetch exercise details'
      };
    }
  },

  /**
   * Get all available exercises with optional filters
   * @param {Object} filters - Filter options
   * @returns {Promise} Exercise results
   */
  getAllExercises: async (filters = {}) => {
    try {
      const params = {};
      if (filters?.category) params.category = filters?.category;
      if (filters?.muscle) params.muscle = filters?.muscle;
      if (filters?.equipment) params.equipment = filters?.equipment;
      if (filters?.difficulty) params.difficulty = filters?.difficulty;

      const response = await rapidApiClient?.get('/exercises', { params });
      return {
        data: response?.data?.map(exercise => ({
          id: `api-${exercise?.id}`,
          name: exercise?.name,
          description: exercise?.description || '',
          category: exercise?.category?.toLowerCase() || 'strength',
          difficulty: exercise?.difficulty?.toLowerCase() || 'intermediate',
          muscleGroups: exercise?.primaryMuscles || [],
          secondaryMuscles: exercise?.secondaryMuscles || [],
          equipmentNeeded: exercise?.equipment ? [exercise?.equipment] : [],
          instructions: exercise?.instructions || [],
          videoUrl: exercise?.videoUrl || null,
          imageUrl: exercise?.images?.[0] || null,
          source: 'rapidapi',
          isExternal: true
        })) || [],
        error: null
      };
    } catch (error) {
      console.error('RapidAPI get all exercises error:', error);
      return {
        data: [],
        error: error?.response?.data?.message || error?.message || 'Failed to fetch exercises'
      };
    }
  },

  /**
   * Check if API key is configured
   * @returns {boolean} True if API key exists
   */
  isConfigured: () => {
    return !!RAPIDAPI_KEY && RAPIDAPI_KEY !== 'your-rapidapi-gym-fit-key-here';
  }
};

/**
 * MyFitnessPal API Service
 * Provides access to food database and nutritional information
 */
export const myFitnessPalService = {
  /**
   * Search for foods by query
   * @param {string} query - Search query for food items
   * @returns {Promise} Food search results with nutritional data
   */
  searchFoods: async (query) => {
    try {
      if (!query || query?.trim() === '') {
        return { data: [], error: null };
      }

      const response = await myFitnessPalClient?.get('/nutrition/search', {
        params: { query: query?.trim() }
      });

      const foods = response?.data?.items || response?.data || [];
      
      return {
        data: foods?.map(food => ({
          id: food?.id || `mfp-${Date.now()}-${Math.random()}`,
          name: food?.name || food?.food_name || 'Unknown Food',
          calories: parseFloat(food?.calories || food?.nf_calories || 0),
          protein: parseFloat(food?.protein || food?.nf_protein || 0),
          carbs: parseFloat(food?.carbohydrates || food?.nf_total_carbohydrate || 0),
          fat: parseFloat(food?.fat || food?.nf_total_fat || 0),
          servingSize: food?.serving_size || food?.serving_qty || 1,
          servingUnit: food?.serving_unit || 'serving',
          brand: food?.brand_name || null,
          verified: food?.verified || false,
          source: 'myfitnesspal'
        })) || [],
        error: null
      };
    } catch (error) {
      console.error('MyFitnessPal search error:', error);
      return {
        data: [],
        error: error?.response?.data?.message || error?.message || 'Failed to search foods'
      };
    }
  },

  /**
   * Get detailed nutritional information for a specific food
   * @param {string} foodId - Food item ID
   * @returns {Promise} Detailed food nutritional data
   */
  getFoodDetails: async (foodId) => {
    try {
      const response = await myFitnessPalClient?.get(`/nutrition/details/${foodId}`);
      const food = response?.data;
      
      return {
        data: {
          id: food?.id || foodId,
          name: food?.name || food?.food_name,
          calories: parseFloat(food?.calories || food?.nf_calories || 0),
          protein: parseFloat(food?.protein || food?.nf_protein || 0),
          carbs: parseFloat(food?.carbohydrates || food?.nf_total_carbohydrate || 0),
          fat: parseFloat(food?.fat || food?.nf_total_fat || 0),
          fiber: parseFloat(food?.fiber || food?.nf_dietary_fiber || 0),
          sugar: parseFloat(food?.sugar || food?.nf_sugars || 0),
          sodium: parseFloat(food?.sodium || food?.nf_sodium || 0),
          servingSize: food?.serving_size || food?.serving_qty || 1,
          servingUnit: food?.serving_unit || 'serving',
          brand: food?.brand_name || null,
          verified: food?.verified || false,
          source: 'myfitnesspal'
        },
        error: null
      };
    } catch (error) {
      console.error('MyFitnessPal food details error:', error);
      return {
        data: null,
        error: error?.response?.data?.message || error?.message || 'Failed to fetch food details'
      };
    }
  },

  /**
   * Get autocomplete suggestions for food search
   * @param {string} query - Partial search query
   * @returns {Promise} Autocomplete suggestions
   */
  getAutocompleteSuggestions: async (query) => {
    try {
      if (!query || query?.trim()?.length < 2) {
        return { data: [], error: null };
      }

      const response = await myFitnessPalClient?.get('/nutrition/autocomplete', {
        params: { query: query?.trim() }
      });

      return {
        data: response?.data?.suggestions || response?.data || [],
        error: null
      };
    } catch (error) {
      console.error('MyFitnessPal autocomplete error:', error);
      return {
        data: [],
        error: error?.response?.data?.message || error?.message || 'Failed to get suggestions'
      };
    }
  },

  /**
   * Search foods by barcode
   * @param {string} barcode - Product barcode (UPC/EAN)
   * @returns {Promise} Food item matching the barcode
   */
  searchByBarcode: async (barcode) => {
    try {
      const response = await myFitnessPalClient?.get('/nutrition/barcode', {
        params: { code: barcode }
      });

      const food = response?.data;
      
      return {
        data: {
          id: food?.id || `mfp-barcode-${barcode}`,
          name: food?.name || food?.food_name,
          calories: parseFloat(food?.calories || food?.nf_calories || 0),
          protein: parseFloat(food?.protein || food?.nf_protein || 0),
          carbs: parseFloat(food?.carbohydrates || food?.nf_total_carbohydrate || 0),
          fat: parseFloat(food?.fat || food?.nf_total_fat || 0),
          servingSize: food?.serving_size || food?.serving_qty || 1,
          servingUnit: food?.serving_unit || 'serving',
          brand: food?.brand_name || null,
          barcode: barcode,
          verified: true,
          source: 'myfitnesspal'
        },
        error: null
      };
    } catch (error) {
      console.error('MyFitnessPal barcode search error:', error);
      return {
        data: null,
        error: error?.response?.data?.message || error?.message || 'Failed to find food by barcode'
      };
    }
  },

  /**
   * Check if MyFitnessPal API is configured
   * @returns {boolean} True if API key exists
   */
  isConfigured: () => {
    return !!MYFITNESSPAL_API_KEY && MYFITNESSPAL_API_KEY !== 'your-rapidapi-myfitnesspal-key-here';
  }
};

export default rapidApiGymFitService;
